/**
 * Bootstrap data injected by the server into the HTML before paint.
 *
 * The Express server reads tenant settings (site brand, theme, hero, contact,
 * features) and writes them into the HTML as `window.__HM_BRAND__` /
 * `window.__HM_FEATURES__` *before* the React bundle parses. The point is to
 * eliminate "Flash of Default Content" (FODC) — the brief moment where the
 * page renders with placeholder text / default theme / wrong logo until the
 * client's first API fetch finishes.
 *
 * This helper provides the canonical "read with full fallback chain" path so
 * the same priority order (server → localStorage → default) is used
 * everywhere a brandable setting is consumed.
 *
 * Cache invalidation: the server clears its per-tenant HTML cache whenever an
 * admin saves via /api/admin/site-settings or /api/admin/site-config, so a
 * change in the admin panel reflects on the very next page request. No CDN
 * flushes, no manual refreshes.
 */

const SERVER_BRAND_KEY = '__HM_BRAND__';

/** Safely read `window.__HM_BRAND__` — returns `null` when unavailable. */
function getServerBrand() {
    try {
        if (typeof window === 'undefined') return null;
        const b = window[SERVER_BRAND_KEY];
        return b && typeof b === 'object' ? b : null;
    } catch { return null; }
}

/**
 * Resolve a brandable setting.
 *
 *   priority: localStorage[storageKey]  →  window.__HM_BRAND__[serverPath]  →  fallback
 *
 * index.js seeds localStorage from the fresh server payload before anything
 * renders, so on page load both hold the same value. After that localStorage
 * is the one that moves: an admin save writes it (and the `storage` event
 * carries it to other tabs). The server copy is a snapshot from page load.
 * Reading the snapshot first (as this did until 2026-10-21) showed the OLD
 * home design after a save — and the next Page Management toggle, which saves
 * hm_hero_banner from localStorage, had been re-seeded with that snapshot and
 * quietly switched the site back to the old design.
 *
 * @param {string}      storageKey   localStorage key (e.g. 'hm_hero_banner')
 * @param {string|null} serverPath   property on window.__HM_BRAND__ (e.g. 'hero')
 * @param {*}           fallback     default to return if neither source has a value
 * @returns the resolved value, shallow-merged with `fallback` when both are objects.
 */
export function loadBranded(storageKey, serverPath, fallback) {
    // Shallow-merge with the caller's fallback so missing fields still fall
    // back to their defaults (new fields can be added client-side safely).
    const merge = (v) => ((fallback && typeof fallback === 'object' && v && typeof v === 'object')
        ? { ...fallback, ...v } : v);
    try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed !== null && parsed !== undefined) return merge(parsed);
        }
    } catch { /* private mode / bad JSON — use the server copy */ }
    const brand = getServerBrand();
    if (brand && serverPath && brand[serverPath] && typeof brand[serverPath] === 'object') {
        const fromServer = brand[serverPath];
        try { localStorage.setItem(storageKey, JSON.stringify(fromServer)); } catch { /* private mode */ }
        return merge(fromServer);
    }
    return fallback;
}

// localStorage key → window.__HM_BRAND__ property (the keys index.js seeds).
const SERVER_PATH_FOR_KEY = {
    hm_theme_custom: 'theme', hm_hero_banner: 'hero', hm_page_contact: 'contact', hm_typography: 'typography',
};

/** Keep the page-load snapshot in step with an admin save in THIS tab, for
 *  any code that still reads window.__HM_BRAND__ directly. */
export function syncServerBrand(storageKey, value) {
    const brand = getServerBrand();
    const prop = SERVER_PATH_FOR_KEY[storageKey];
    if (!brand || !prop) return;
    try {
        if (value && typeof value === 'object') brand[prop] = value;
        else delete brand[prop];
    } catch { /* frozen object — localStorage still holds the value */ }
}

/**
 * Read the server-injected site brand (`siteName`, `logoUrl`) — used by code
 * paths that want the brand details without going through localStorage at all.
 */
export function getServerSiteBrand() {
    const brand = getServerBrand();
    if (!brand) return null;
    const { siteName, logoUrl } = brand;
    return (siteName || logoUrl) ? { siteName: siteName || '', logoUrl: logoUrl || '' } : null;
}
